import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getAIAudios } from '@/features/orders/ordering/services/orderMediaService.js';
import {
  fetchTask,
  selectOrderVideo,
} from '@/features/orders/ordering/services/orderWorkflowService.js';
import {
  fetchOrderAutomation,
  fetchOrderVideoProductionJobs,
  downloadVideoProductionFile,
} from '@/features/automation/videoEditing/services/productionJobService.js';
import LoadingIndicator from '@/components/LoadingIndicator.js';
import './DetailViewPage.scss';
import { Button } from 'react-bootstrap';
import {
  PERSON_TYPES,
  ROLE_TYPES,
  VIDEO_TYPES,
  GENDER_TYPES,
} from '@/constants/constants.js';
import DuplicateAudio from './components/DuplicateAudio.js';
import VideoJobsSection from './components/VideoJobsSection.js';
import ImageFixModal from '../../components/ImageFixModal/ImageFixModal.js';
import {
  buildVideoProductionCreatePath,
  canUseVideoProductionEntry,
  shouldShowVideoProductionButton,
} from '@/features/automation/videoEditing/utils/productionEntryUtils.js';
import VideoOutputCard from '@/components/media/VideoOutputCard.js';
import {
  VIDEO_PRODUCTION_JOB_STATUS,
  VIDEO_PRODUCTION_JOB_STATUS_LABELS,
} from '@/features/automation/videoEditing/constants.js';
import {
  downloadJianyingJob,
  fetchJianyingJobs,
  fetchJianyingOrderAutomation,
  retryJianyingJob,
} from '@/features/automation/jianying/services/jianyingJobService.js';
import {
  JIANYING_JOB_STATUS,
  getJianyingJobStatusLabel,
  getJianyingStatusTone,
} from '@/features/automation/jianying/constants/jianyingJobStatus.js';
import {
  buildJianyingCreatePath,
  shouldShowJianyingProductionButton,
} from '@/features/automation/jianying/utils/jianyingEntryUtils.js';

const ALIYUN_ACTIVE_STAGES = [
  VIDEO_PRODUCTION_JOB_STATUS.SUBMITTING,
  VIDEO_PRODUCTION_JOB_STATUS.SUBMITTED,
  VIDEO_PRODUCTION_JOB_STATUS.INIT,
  VIDEO_PRODUCTION_JOB_STATUS.QUEUING,
  VIDEO_PRODUCTION_JOB_STATUS.PROCESSING,
];
const JIANYING_ACTIVE_STAGES = [
  JIANYING_JOB_STATUS.QUEUED,
  JIANYING_JOB_STATUS.GENERATING_DRAFT,
  JIANYING_JOB_STATUS.DRAFT_READY,
  JIANYING_JOB_STATUS.EXPORTING,
  JIANYING_JOB_STATUS.EXPORTED,
  JIANYING_JOB_STATUS.UPLOADING,
];
const JIANYING_STATUS_OPTIONS = Object.values(JIANYING_JOB_STATUS).map((status) => ({
  value: status,
  label: getJianyingJobStatusLabel(status),
}));
const VIDEO_PRODUCTION_STATUS_OPTIONS = Object.values(
  VIDEO_PRODUCTION_JOB_STATUS
).map((status) => ({
  value: status,
  label: VIDEO_PRODUCTION_JOB_STATUS_LABELS[status] || status,
}));

const getProgress = (stages, status) => {
  const index = stages.indexOf(status);
  return index < 0 ? 0 : Math.round(((index + 1) / stages.length) * 100);
};

const getAliyunStatusTone = (status) => {
  if (status === VIDEO_PRODUCTION_JOB_STATUS.SUCCESS) return 'success';
  if (
    status === VIDEO_PRODUCTION_JOB_STATUS.FAILED
    || status === VIDEO_PRODUCTION_JOB_STATUS.SUBMIT_FAILED
  ) return 'danger';
  return ALIYUN_ACTIVE_STAGES.includes(status) ? 'progress' : 'muted';
};

const getActionErrorMessage = (error, fallback) => (
  error.response?.data?.message || error.message || fallback
);

const DetailViewPage = () => {
  const { client_id } = useParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [clientInfo, setClientInfo] = useState(null);
  const role = Number(sessionStorage.getItem('role_id'));
  const [isDownloadLoading, setIsDownloadLoading] = useState(false);

  const [isDuplicateSoundLoading, setIsDuplicateSoundLoading] = useState(false);
  const [isDuplicateSoundVisible, setIsDuplicateSoundVisible] = useState(false);
  const [aiAudios, setAIAudios] = useState([]);
  const [automation, setAutomation] = useState(null);
  const [jianyingAutomation, setJianyingAutomation] = useState(null);
  const [jianyingJobs, setJianyingJobs] = useState([]);
  const [jianyingJobsTotal, setJianyingJobsTotal] = useState(0);
  const [jianyingJobsLoading, setJianyingJobsLoading] = useState(true);
  const [jianyingJobsMessage, setJianyingJobsMessage] = useState('');
  const [jianyingStatusFilter, setJianyingStatusFilter] = useState(
    JIANYING_JOB_STATUS.SUCCESS
  );
  const jianyingJobsRequestId = useRef(0);
  const [videoProductionJobs, setVideoProductionJobs] = useState([]);
  const [videoProductionTotal, setVideoProductionTotal] = useState(0);
  const [videoProductionJobsLoading, setVideoProductionJobsLoading] = useState(true);
  const [videoProductionMessage, setVideoProductionMessage] = useState('');
  const [videoProductionStatusFilter, setVideoProductionStatusFilter] = useState(
    VIDEO_PRODUCTION_JOB_STATUS.SUCCESS
  );
  const videoProductionJobsRequestId = useRef(0);

  // 修复失败图片弹窗相关状态
  const [showFixFailModal, setShowFixFailModal] = useState(false);
  const canUseProductionTools = canUseVideoProductionEntry(role);
  const showVideoProductionButton = shouldShowVideoProductionButton({
    role,
    automation,
  });
  const showJianyingProductionButton = shouldShowJianyingProductionButton({
    role,
    automation: jianyingAutomation,
  });
  const showJianyingVideoSection = canUseProductionTools && (
    jianyingJobsLoading
    || jianyingJobs.length > 0
    || Boolean(jianyingJobsMessage)
    || Boolean(jianyingAutomation?.supported)
  );
  const showVideoProductionSection = canUseProductionTools && (
    videoProductionJobsLoading
    || videoProductionJobs.length > 0
    || Boolean(videoProductionMessage)
    || Boolean(automation?.supported)
  );

  const loadJianyingJobs = useCallback(async ({ silent = false } = {}) => {
    if (!canUseProductionTools) return;
    const requestId = jianyingJobsRequestId.current + 1;
    jianyingJobsRequestId.current = requestId;
    if (!silent) {
      setJianyingJobsLoading(true);
      setJianyingJobsMessage('');
    }
    try {
      const result = await fetchJianyingJobs({
        client_id: client_id,
        limit: 20,
        status: jianyingStatusFilter || undefined,
      });
      if (jianyingJobsRequestId.current !== requestId) return;
      setJianyingJobs(result.items);
      setJianyingJobsTotal(result.total);
    } catch (error) {
      if (!silent && jianyingJobsRequestId.current === requestId) {
        setJianyingJobsMessage(
          error.response?.data?.message || error.message || '读取剪映成片失败'
        );
      }
    } finally {
      if (!silent && jianyingJobsRequestId.current === requestId) {
        setJianyingJobsLoading(false);
      }
    }
  }, [canUseProductionTools, client_id, jianyingStatusFilter]);

  const loadVideoProductionJobs = useCallback(async () => {
    if (!canUseProductionTools) return;
    const requestId = videoProductionJobsRequestId.current + 1;
    videoProductionJobsRequestId.current = requestId;
    setVideoProductionJobsLoading(true);
    setVideoProductionMessage('');
    try {
      const jobsData = await fetchOrderVideoProductionJobs(client_id, {
        limit: 20,
        status: videoProductionStatusFilter || undefined,
      });
      if (videoProductionJobsRequestId.current !== requestId) return;
      setVideoProductionJobs(jobsData.items || []);
      setVideoProductionTotal(
        jobsData.pagination?.total || jobsData.items?.length || 0
      );
    } catch (error) {
      if (videoProductionJobsRequestId.current === requestId) {
        setVideoProductionMessage(
          error.response?.data?.message || error.message || '读取阿里云视频失败'
        );
      }
    } finally {
      if (videoProductionJobsRequestId.current === requestId) {
        setVideoProductionJobsLoading(false);
      }
    }
  }, [canUseProductionTools, client_id, videoProductionStatusFilter]);

  useEffect(() => {
    async function fetchData() {
      try {
        const taskData = await fetchTask(client_id);

        if (taskData.client_id !== '') {
          setClientInfo(taskData);

          if (canUseProductionTools) {
            try {
              const automationData = await fetchOrderAutomation(client_id);
              setAutomation(automationData);
            } catch (error) {
              console.error('获取自动化配置失败:', error);
            }
            try {
              const automationData = await fetchJianyingOrderAutomation(client_id);
              setJianyingAutomation(automationData);
            } catch (error) {
              console.error('获取剪映自动化配置失败:', error);
            }
          }

          if (role === ROLE_TYPES.制作人员 || role === ROLE_TYPES.管理员) {
            const aiAudios = await getAIAudios(taskData.batch_id);
            setAIAudios(aiAudios);
          }
        }
      } catch (error) {
        console.error('获取任务数据失败:', error);
      } finally {
        setIsLoading(false);
        window.scrollTo({
          top: 0,
          behavior: 'smooth',
        });
      }
    }

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client_id]);

  useEffect(() => {
    if (!canUseProductionTools) return undefined;
    loadJianyingJobs();
    const timer = window.setInterval(
      () => loadJianyingJobs({ silent: true }),
      10000
    );
    return () => {
      jianyingJobsRequestId.current += 1;
      window.clearInterval(timer);
    };
  }, [canUseProductionTools, loadJianyingJobs]);

  useEffect(() => {
    if (!canUseProductionTools) return undefined;
    loadVideoProductionJobs();
    return () => {
      videoProductionJobsRequestId.current += 1;
    };
  }, [canUseProductionTools, loadVideoProductionJobs]);

  // 修改handleDownload为：点击后先刷新数据，然后弹出修复弹窗
  const handleDownload = async () => {
    setIsDownloadLoading(true);
    try {
      // 获取最新的任务数据
      const taskData = await fetchTask(client_id);
      if (taskData.client_id !== '') {
        setClientInfo(taskData);
      }
    } catch (error) {
      console.error('刷新任务数据失败:', error);
    } finally {
      setIsDownloadLoading(false);
    }

    setShowFixFailModal(true);
  };

  const handleSelectVideo = async (source, job) => {
    const selectedVideo = await selectOrderVideo(client_id, source, job.id);
    setClientInfo((current) => ({
      ...current,
      selected_video: selectedVideo,
    }));
    return selectedVideo;
  };

  const handleDuplicateSoundVIsible = async () => {
    try {
      setIsDuplicateSoundVisible(!isDuplicateSoundVisible);
    } catch (error) {
      console.error('Error downloading files:', error);
    }
  };

  // 渲染前判空，防止 clientInfo 为 null
  if (isLoading) {
    return <LoadingIndicator />;
  }
  if (!clientInfo) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: '#888' }}>
        未获取到客户信息
      </div>
    );
  }

  return (
    <div className="detail-view-page">
      <div className="header">
        <h3>制作</h3>
        {canUseProductionTools &&
          (isDownloadLoading ? (
            <LoadingIndicator
              label={'资料正在下载，请等待'}
              containerClassName={'flex-row'}
              border={true}
            />
          ) : (
            <div>
              <Button variant="primary" onClick={() => handleDownload()}>
                订单资料下载
              </Button>{' '}
              <Button
                variant="primary"
                onClick={() => handleDuplicateSoundVIsible()}
              >
                复刻声音
              </Button>
              {showVideoProductionButton && (
                <>
                  {' '}
                  <Button
                    variant="primary"
                    onClick={() =>
                      navigate(buildVideoProductionCreatePath(client_id))
                    }
                  >
                    自动化制作
                  </Button>
                </>
              )}
              {showJianyingProductionButton && (
                <>
                  {' '}
                  <Button
                    variant="primary"
                    onClick={() => navigate(buildJianyingCreatePath(client_id))}
                  >
                    剪映制作
                  </Button>
                </>
              )}
            </div>
          ))}
      </div>

      {/* 修复失败图片弹窗 */}
      <ImageFixModal
        show={showFixFailModal}
        images={clientInfo?.images || []}
        clientInfo={clientInfo}
        client_id={client_id}
        onClose={() => setShowFixFailModal(false)}
      />

      {isDuplicateSoundVisible && (
        <DuplicateAudio
          client_id={client_id}
          setIsDuplicateSoundLoading={setIsDuplicateSoundLoading}
          isDuplicateSoundLoading={isDuplicateSoundLoading}
          required={false}
          onlyUpload={true}
          clientInfo={clientInfo}
          aiAudios={aiAudios}
          setAIAudios={setAIAudios}
        />
      )}

      {showJianyingVideoSection && (
        <VideoJobsSection
          count={jianyingJobs.length}
          emptyText="暂无剪映成片"
          loading={jianyingJobsLoading}
          loadingText="正在读取剪映成片"
          message={jianyingJobsMessage}
          onRefresh={() => loadJianyingJobs()}
          onStatusChange={setJianyingStatusFilter}
          statusOptions={JIANYING_STATUS_OPTIONS}
          statusValue={jianyingStatusFilter}
          title="剪映成片"
          total={jianyingJobsTotal}
        >
          {jianyingJobs.map((job) => (
            <VideoOutputCard
              key={job.id}
              errorMessage={job.error_message}
              isFailed={job.status === JIANYING_JOB_STATUS.FAILED}
              isSuccess={
                job.status === JIANYING_JOB_STATUS.SUCCESS
                && Boolean(job.video_url)
              }
              name={job.generated_draft_name || job.id}
              onActionError={(error, fallback) =>
                setJianyingJobsMessage(getActionErrorMessage(error, fallback))}
              onChanged={(_result, action) => {
                if (action === 'retry') loadJianyingJobs();
              }}
              onDownload={() => downloadJianyingJob(job.id)}
              onRetry={() => retryJianyingJob(job.id)}
              onSelect={() => handleSelectVideo('jianying', job)}
              progress={getProgress(JIANYING_ACTIVE_STAGES, job.status)}
              selected={
                clientInfo.selected_video?.source === 'jianying'
                && String(clientInfo.selected_video?.job_id) === String(job.id)
              }
              statusLabel={getJianyingJobStatusLabel(job.status)}
              statusTone={getJianyingStatusTone(job.status)}
              time={job.finished_at || job.create_time}
              title={job.template_name || '剪映视频'}
              videoUrl={job.video_url}
            />
          ))}
        </VideoJobsSection>
      )}

      {showVideoProductionSection && (
        <VideoJobsSection
          count={videoProductionJobs.length}
          emptyText="暂无阿里云视频"
          loading={videoProductionJobsLoading}
          loadingText="正在读取阿里云视频"
          message={videoProductionMessage}
          onRefresh={loadVideoProductionJobs}
          onStatusChange={setVideoProductionStatusFilter}
          statusOptions={VIDEO_PRODUCTION_STATUS_OPTIONS}
          statusValue={videoProductionStatusFilter}
          title="阿里云视频订单"
          total={videoProductionTotal}
        >
          {videoProductionJobs.map((job) => (
            <VideoOutputCard
              key={job.id}
              errorMessage={job.error_message}
              isFailed={
                job.status === VIDEO_PRODUCTION_JOB_STATUS.FAILED
                || job.status === VIDEO_PRODUCTION_JOB_STATUS.SUBMIT_FAILED
              }
              isSuccess={
                job.status === VIDEO_PRODUCTION_JOB_STATUS.SUCCESS
                && Boolean(job.output_url)
              }
              name={job.id}
              onActionError={(error, fallback) =>
                setVideoProductionMessage(getActionErrorMessage(error, fallback))}
              onDownload={() => downloadVideoProductionFile(job.id)}
              onSelect={() => handleSelectVideo('aliyun', job)}
              progress={getProgress(ALIYUN_ACTIVE_STAGES, job.status)}
              selected={
                clientInfo.selected_video?.source === 'aliyun'
                && String(clientInfo.selected_video?.job_id) === String(job.id)
              }
              statusLabel={
                VIDEO_PRODUCTION_JOB_STATUS_LABELS[job.status] || job.status
              }
              statusTone={getAliyunStatusTone(job.status)}
              time={job.finished_at || job.create_time}
              title={job.template_name || '阿里云视频'}
              videoUrl={job.output_url}
            />
          ))}
        </VideoJobsSection>
      )}

      {!canUseProductionTools && clientInfo.selected_video?.video_url && (
        <section className="selected-order-video">
          <div className="selected-order-video__header">
            <h4>已选成片</h4>
          </div>
          <VideoOutputCard
            isSuccess
            name={clientInfo.selected_video.name || '已选成片'}
            selected
            statusLabel="已选用"
            statusTone="success"
            time={clientInfo.selected_video.created_at}
            title={
              clientInfo.selected_video.source === 'jianying'
                ? '剪映视频'
                : '阿里云视频'
            }
            videoUrl={clientInfo.selected_video.video_url}
          />
        </section>
      )}

      <div className="row g-3">
        <div className="field-container">
          <div className="title-span">逝者: </div>
          <div className="content-text">{clientInfo?.clientName}</div>
        </div>
        <div className="field-container">
          <div className="title-span">仪式时间: </div>
          <div className="content-text">{clientInfo?.ceremonyDate}</div>
        </div>
        <div className="field-container">
          <div className="title-span">套餐标准: </div>
          <div className="content-text">{clientInfo?.videoType}</div>
        </div>
        <div className="field-container">
          <div className="title-span">逝者性别: </div>
          <div className="content-text">
            {clientInfo?.gender === GENDER_TYPES.男
              ? '男'
              : clientInfo?.gender === GENDER_TYPES.女
              ? '女'
              : '未知'}
          </div>
        </div>
        <div className="field-container">
          <div className="title-span">逝者生平: </div>
          <div className="content-text">{clientInfo?.texts}</div>
        </div>
        {clientInfo?.speech_text && (
          <div className="field-container">
            <div className="title-span">致辞: </div>
            <div className="content-text">{clientInfo?.speech_text}</div>
          </div>
        )}
        {clientInfo?.farewell_text && (
          <div className="field-container">
            <div className="title-span">告别: </div>
            <div className="content-text">{clientInfo?.farewell_text}</div>
          </div>
        )}
        <div className="field-container">
          <div className="title-span">讲述逝者生平: </div>
          <div className="content-text">{clientInfo?.firstOrThirdPerson}</div>
        </div>
        {clientInfo?.firstOrThirdPerson === PERSON_TYPES.THIRD_PERSON && (
          <>
            <div className="field-container">
              <div className="title-span">讲述者为逝者的: </div>
              <div className="content-text">{clientInfo?.relationship}</div>
            </div>
            {clientInfo?.videoType !== VIDEO_TYPES.数字音容版 &&
              clientInfo?.videoType !== VIDEO_TYPES.数字音容Plus &&
              clientInfo?.videoType !== VIDEO_TYPES.AI念影版 &&
              clientInfo?.videoType !== VIDEO_TYPES.AI念影版25年版本 &&
              clientInfo && (
                <div className="field-container">
                  <div className="title-span">讲述者音频: </div>
                  {clientInfo?.narrator_audio[0] ? (
                    <div className="content-text">
                      <audio controls>
                        <source
                          src={clientInfo?.narrator_audio[0].content}
                          type="audio/mpeg"
                        />
                        您的浏览器不支持音频元素。
                      </audio>
                      <div className="text-container">
                        <strong>声音文本: </strong>
                        {clientInfo?.narrator_audio[0].description}
                      </div>
                    </div>
                  ) : (
                    '未上传音频，使用音库音色'
                  )}
                </div>
              )}
          </>
        )}
        {clientInfo?.videoType !== VIDEO_TYPES.数字音容版 &&
          clientInfo?.videoType !== VIDEO_TYPES.数字音容Plus &&
          clientInfo?.videoType !== VIDEO_TYPES.AI念影版 &&
          clientInfo?.videoType !== VIDEO_TYPES.AI念影版25年版本 && (
            <div className="field-container">
              <div className="title-span">逝者声音: </div>

              {clientInfo && clientInfo?.deceased_audio[0] ? (
                <div className="content-text">
                  <audio controls>
                    <source
                      src={clientInfo?.deceased_audio[0].content}
                      type="audio/mpeg"
                    />
                    您的浏览器不支持音频元素。
                  </audio>
                  <div className="text-container">
                    <strong>声音文本: </strong>
                    {clientInfo?.deceased_audio[0].description}
                  </div>
                </div>
              ) : (
                '未上传音频，使用音库音色'
              )}
            </div>
          )}
        <div className="field-container">
          <div className="title-span">逝者照片: </div>
          <div className="content-text">
            {clientInfo?.images.map((image, index) => (
              <div className="image-container" key={index}>
                <div>视频排序：<strong>{image.sort_order}</strong></div>
                <div className="image-preview ">
                  <img src={image.content} alt={`图片 ${index + 1}`} />
                </div>
                <div className="imageDescription-span">
                  {image.description}{' '}
                </div>
                <div>
                  <input
                    type="checkbox"
                    id={`dynamic-checkbox-${index}`}
                    checked={image.image_dynamic_AI || false}
                    disabled={true}
                  />
                  <label htmlFor={`dynamic-checkbox-${index}`}>
                    AI动态化照片
                  </label>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="field-container">
          <div className="title-span">主事人手机号: </div>
          <div className="content-text">
            {clientInfo?.telephone ? clientInfo?.telephone : ''}
          </div>
        </div>
        <div className="field-container">
          <div className="title-span">是否在仪式中使用视频: </div>
          <div className="content-text">
            {clientInfo?.useInCeremony === true
              ? '是'
              : clientInfo?.useInCeremony === false
              ? '否'
              : '未知'}
          </div>
        </div>
        <div className="field-container">
          <div className="title-span">业务员: </div>
          <div className="content-text">
            {clientInfo?.salesPerson ? clientInfo?.salesPerson : ''}
          </div>
        </div>
        <div className="field-container">
          <div className="title-span">视频备注: </div>
          <div className="content-text">
            {clientInfo?.notes ? clientInfo?.notes : '暂无'}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DetailViewPage;
